import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useBookingRealtimeRefresh } from "@/lib/useBookingRealtimeRefresh";
import { formatStayAmount } from "@/lib/money";
import { useCurrentUser } from "@/lib/auth";
import { canWriteGuestPitchBooking } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { ClickableTableRow } from "@/components/ui/ClickableTableRow";
import { SortableTableHeader } from "@/components/ui/SortableTableHeader";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Table } from "@/components/ui/Table";
import {
  matchesBookingSearch,
  mergeBookingsWithInquiries,
  nextSortState,
  sortBookings,
  type BookingSortKey,
  type SortDir,
} from "@/lib/bookingTableUtils";
import { useLoadMoreOnScroll } from "@/hooks/useLoadMoreOnScroll";
import { addDaysLocal, formatIsoDateShort, todayLocal } from "@/lib/bookingDates";
import { resolveGuestName } from "@/lib/guestDisplay";
import { PitchPlanCell } from "@/components/bookings/PitchPlanCell";
import { useGuestById } from "@/lib/useGuestById";
import type { Booking, Guest, Pitch, PitchCategory } from "@/types/camping";
import { CreateBookingModal } from "@/pages/bookings/CreateBookingModal";
import { PhoneBookingHoldModal } from "@/pages/bookings/PhoneBookingHoldModal";
import { EditBookingModal } from "@/pages/bookings/EditBookingModal";
import { GuestStayModal } from "@/components/bookings/GuestStayModal";
import { InquiryDetailModal } from "@/components/bookings/InquiryDetailModal";
import {
  InquiryEmailModal,
  type InquiryResponseIntent,
} from "@/components/bookings/InquiryEmailModal";
import { PlaceBanConfirmModal } from "@/components/bookings/PlaceBanConfirmModal";
import { EditGuestModal } from "@/pages/guests/EditGuestModal";
import { EditPitchModal } from "@/pages/pitches/EditPitchModal";
import {
  confirmBookingInquiry,
  confirmRejectBookingInquiry,
  rejectBookingInquiry,
} from "@/lib/bookingInquiryActions";
import {
  bookingListStatusTab,
  bookingsListApiUrl,
  type BookingListStatusTab,
} from "@/lib/bookingNavigation";
import {
  collectPlaceBanGuestIds,
  liftPlaceBanForGuests,
  shouldConfirmPlaceBanBeforeInquiry,
} from "@/lib/placeBanActions";

export function BookingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteGuestPitchBooking(groups);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [inquiries, setInquiries] = useState<Booking[]>([]);
  const [requestedCount, setRequestedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [categories, setCategories] = useState<PitchCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [phoneHoldModalOpen, setPhoneHoldModalOpen] = useState(false);
  const [stayModalBooking, setStayModalBooking] = useState<Booking | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [selectedPitch, setSelectedPitch] = useState<Pitch | null>(null);
  const [optimizing, setOptimizing] = useState(false);
  const [confirmLoadingId, setConfirmLoadingId] = useState<string | null>(null);
  const [rejectLoadingId, setRejectLoadingId] = useState<string | null>(null);
  const [inquiryDetailModal, setInquiryDetailModal] = useState<Booking | null>(null);

  function applyBookingUpdate(updated: Booking) {
    setBookings((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
    setInquiries((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
    setInquiryDetailModal((prev) => (prev?.id === updated.id ? updated : prev));
    setStayModalBooking((prev) => (prev?.id === updated.id ? updated : prev));
  }
  const [inquiryEmailModal, setInquiryEmailModal] = useState<{
    booking: Booking;
    intent: InquiryResponseIntent;
  } | null>(null);
  const [placeBanConfirmModal, setPlaceBanConfirmModal] = useState<Booking | null>(null);
  const [placeBanConfirmLoading, setPlaceBanConfirmLoading] = useState(false);
  const statusFilter = (searchParams.get("status") ?? "requested") as BookingListStatusTab;
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<BookingSortKey>("arrival");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const loadInquiries = useCallback(() => {
    void Promise.allSettled([
      api.get<Booking[]>("/api/v1/bookings/inquiries"),
      api.get<{ count: number }>("/api/v1/bookings/inquiries/count"),
    ]).then(([listResult, countResult]) => {
      if (listResult.status === "fulfilled") setInquiries(listResult.value);
      if (countResult.status === "fulfilled") setRequestedCount(countResult.value.count);
    });
    void api
      .get<{ count: number }>("/api/v1/bookings/rejected/count")
      .then((data) => setRejectedCount(data.count))
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);

    const isRequestedTab = statusFilter === "requested";
    const primary = isRequestedTab
      ? api.get<Booking[]>("/api/v1/bookings/inquiries")
      : api.get<Booking[]>(bookingsListApiUrl(statusFilter));

    void primary
      .then((data) => {
        if (isRequestedTab) {
          setInquiries(data);
        } else {
          setBookings(data);
        }
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "Laden fehlgeschlagen"),
      )
      .finally(() => setLoading(false));

    void api
      .get<{ count: number }>("/api/v1/bookings/inquiries/count")
      .then((data) => setRequestedCount(data.count))
      .catch(() => {});

    void api
      .get<{ count: number }>("/api/v1/bookings/rejected/count")
      .then((data) => setRejectedCount(data.count))
      .catch(() => {});

    if (!isRequestedTab) {
      void api
        .get<Booking[]>("/api/v1/bookings/inquiries")
        .then(setInquiries)
        .catch(() => {});
    }

    void api
      .get<Pitch[]>("/api/v1/pitches")
      .then(setPitches)
      .catch(() => {});

    void api
      .get<PitchCategory[]>("/api/v1/pitch-categories")
      .then(setCategories)
      .catch(() => {});
  }, [statusFilter]);

  useEffect(() => {
    setPageMeta({ title: "Buchungen", subtitle: "Reservierungen & Preise" });
    load();
    return () => setPageMeta(null);
  }, [load, setPageMeta]);

  useBookingRealtimeRefresh(() => {
    loadInquiries();
    if (!loading) {
      void api
        .get<Booking[]>(bookingsListApiUrl(statusFilter))
        .then(setBookings)
        .catch(() => {});
    }
  });

  const allBookingsForLookup = useMemo(
    () => mergeBookingsWithInquiries(bookings, inquiries),
    [bookings, inquiries],
  );

  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId) return;

    function openBookingFromDeepLink(booking: Booking) {
      const tab = bookingListStatusTab(booking.status);
      if (booking.status === "requested") {
        setInquiries((prev) => mergeBookingsWithInquiries(prev, [booking]));
        setInquiryDetailModal(booking);
        setStayModalBooking(null);
      } else {
        setBookings((prev) => mergeBookingsWithInquiries(prev, [booking]));
        setStayModalBooking(booking);
        setInquiryDetailModal(null);
      }
      setSearchParams({ status: tab }, { replace: true });
    }

    const match = allBookingsForLookup.find((b) => b.id === openId);
    if (match) {
      openBookingFromDeepLink(match);
      return;
    }
    if (loading) return;

    void api
      .get<Booking>(`/api/v1/bookings/${openId}`)
      .then(openBookingFromDeepLink)
      .catch(() => {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("open");
            return next;
          },
          { replace: true },
        );
      });
  }, [searchParams, loading, allBookingsForLookup, setSearchParams]);

  const guestName = (id: string, booking?: Booking | null) => resolveGuestName(id, { booking });
  const pitchCode = (id: string | null) =>
    id ? (pitches.find((x) => x.id === id)?.code ?? id.slice(0, 8)) : null;

  const bookingPitchLabel = (b: Booking) => {
    const code = pitchCode(b.pitch_id);
    if (code) return code;
    const cat = b.pitch_category_id
      ? categories.find((c) => c.id === b.pitch_category_id)?.name
      : "Ohne Kategorie";
    return `Offen (${cat})`;
  };

  const filteredBookings = useMemo(() => {
    if (statusFilter === "requested") {
      return inquiries;
    }
    if (statusFilter === "rejected") {
      return bookings.filter((b) => b.status === "rejected");
    }
    const merged = mergeBookingsWithInquiries(bookings, inquiries);
    if (statusFilter === "confirmed") {
      return merged.filter((b) =>
        ["confirmed", "reserved", "checked_in"].includes(b.status),
      );
    }
    if (statusFilter === "checked_out") {
      return merged.filter((b) => b.status === "checked_out");
    }
    return merged;
  }, [bookings, inquiries, statusFilter]);

  const searchedBookings = useMemo(() => {
    if (!search.trim()) return filteredBookings;
    return filteredBookings.filter((b) =>
      matchesBookingSearch(b, search, {
        guestName: guestName(b.guest_id, b),
        pitchLabel: bookingPitchLabel(b),
      }),
    );
  }, [filteredBookings, search, pitches, categories]);

  const sortedBookings = useMemo(
    () =>
      sortBookings(searchedBookings, sortKey, sortDir, (b) => guestName(b.guest_id, b), bookingPitchLabel),
    [searchedBookings, sortKey, sortDir, pitches, categories],
  );

  const { visibleItems: visibleBookings, hasMore, sentinelRef, visibleCount } =
    useLoadMoreOnScroll(sortedBookings);

  function handleSortColumn(column: BookingSortKey) {
    const next = nextSortState(sortKey, sortDir, column);
    setSortKey(next.key);
    setSortDir(next.dir);
  }

  const bookingCountLabel = useMemo(() => {
    const total = sortedBookings.length;
    if (total === 0) return "Keine Buchungen im aktuellen Filter";
    const visible = Math.min(visibleCount, total);
    if (search.trim()) {
      if (visible < total) {
        return `${visible} von ${total} Treffern angezeigt`;
      }
      return total === 1 ? "1 Treffer" : `${total} Treffer`;
    }
    if (visible < total) {
      return `${visible} von ${total} Buchungen angezeigt`;
    }
    return total === 1 ? "1 Buchung" : `${total} Buchungen`;
  }, [sortedBookings.length, search, visibleCount]);

  const stayModalGuest = useGuestById(stayModalBooking?.guest_id);
  const inquiryDetailGuest = useGuestById(inquiryDetailModal?.guest_id);

  function openInquiryDetail(booking: Booking) {
    setInquiryDetailModal(booking);
    setStayModalBooking(null);
    setInquiryEmailModal(null);
    setError(null);
  }

  function openInquiryEmail(booking: Booking, intent: InquiryResponseIntent) {
    if (shouldConfirmPlaceBanBeforeInquiry(booking, intent)) {
      setPlaceBanConfirmModal(booking);
      setInquiryDetailModal(null);
      setStayModalBooking(null);
      setError(null);
      return;
    }
    setInquiryEmailModal({ booking, intent });
    setInquiryDetailModal(null);
    setStayModalBooking(null);
    setError(null);
  }

  async function handlePlaceBanConfirmProceed(liftPlaceBan: boolean) {
    const booking = placeBanConfirmModal;
    if (!booking) return;
    setPlaceBanConfirmLoading(true);
    setError(null);
    try {
      if (liftPlaceBan) {
        let guestForBan: Guest | null =
          inquiryDetailGuest?.id === booking.guest_id ? inquiryDetailGuest : null;
        if (!guestForBan) {
          guestForBan = await api.get<Guest>(`/api/v1/guests/${booking.guest_id}`);
        }
        await liftPlaceBanForGuests(collectPlaceBanGuestIds(booking, guestForBan));
        load();
      }
      setPlaceBanConfirmModal(null);
      setInquiryEmailModal({ booking, intent: "confirm" });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Platzverbot konnte nicht aufgehoben werden.");
    } finally {
      setPlaceBanConfirmLoading(false);
    }
  }

  function openBookingFromTable(booking: Booking) {
    if (booking.status === "requested") {
      openInquiryDetail(booking);
      return;
    }
    setStayModalBooking(booking);
  }

  const inquiryDetailPitch = useMemo(
    () =>
      inquiryDetailModal?.pitch_id
        ? pitches.find((p) => p.id === inquiryDetailModal.pitch_id) ?? null
        : null,
    [inquiryDetailModal, pitches],
  );

  const inquiryDetailCategory = useMemo(() => {
    if (!inquiryDetailModal?.pitch_category_id) return null;
    return categories.find((c) => c.id === inquiryDetailModal.pitch_category_id)?.name ?? null;
  }, [inquiryDetailModal, categories]);

  const stayModalPitch = useMemo(
    () =>
      stayModalBooking?.pitch_id
        ? pitches.find((p) => p.id === stayModalBooking.pitch_id) ?? null
        : null,
    [stayModalBooking, pitches],
  );

  const selectedBookingGuest = useGuestById(selectedBooking?.guest_id);

  function openGuestFromBooking(guest: Guest) {
    setSelectedBooking(null);
    setSelectedGuest(guest);
  }

  function openPitchFromBooking(pitch: Pitch) {
    setSelectedBooking(null);
    setSelectedPitch(pitch);
  }

  async function handleConfirmInquiry(
    booking: Booking,
    options?: { skipConfirmationEmail?: boolean },
  ) {
    if (!canEdit || booking.status !== "requested") return;
    setConfirmLoadingId(booking.id);
    setError(null);
    try {
      const updated = await confirmBookingInquiry(booking.id, options);
      setInquiries((prev) => prev.filter((b) => b.id !== updated.id));
      setBookings((prev) => mergeBookingsWithInquiries(prev, [updated]));
      setRequestedCount((prev) => Math.max(0, prev - 1));
      setSearchParams({ status: "confirmed" }, { replace: true });
      setInquiryEmailModal(null);
      setInquiryDetailModal(null);
      setStayModalBooking(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Bestätigung fehlgeschlagen.");
      throw e;
    } finally {
      setConfirmLoadingId(null);
    }
  }

  async function handleRejectInquiry(booking: Booking, skipConfirm = false) {
    if (!canEdit || booking.status !== "requested") return;
    if (!skipConfirm && !confirmRejectBookingInquiry(booking)) return;
    setRejectLoadingId(booking.id);
    setError(null);
    try {
      const updated = await rejectBookingInquiry(booking);
      setInquiries((prev) => prev.filter((b) => b.id !== updated.id));
      setBookings((prev) => mergeBookingsWithInquiries(prev, [updated]));
      setRequestedCount((prev) => Math.max(0, prev - 1));
      setRejectedCount((prev) => prev + 1);
      setSearchParams({ status: "rejected" }, { replace: true });
      setInquiryEmailModal(null);
      setInquiryDetailModal(null);
      setStayModalBooking(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Ablehnung fehlgeschlagen.");
      throw e;
    } finally {
      setRejectLoadingId(null);
    }
  }

  async function runPitchOptimization() {
    const from = todayLocal();
    const to = addDaysLocal(from, 90);
    setOptimizing(true);
    setError(null);
    try {
      const result = await api.post<{ assigned_count: number }>(
        "/api/v1/pitch-assignments/optimize",
        { from_date: from, to_date: to, force_reassign: true },
      );
      if (result.assigned_count === 0) {
        setError(null);
      }
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Optimierung fehlgeschlagen");
    } finally {
      setOptimizing(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="space-y-2">
          <p className="text-sm text-label-secondary">
            Buchungen verwalten und neue Reservierungen anlegen. Zeile anklicken für Details.
          </p>
          <div className="flex flex-wrap gap-2" data-tour="bookings-status-tabs">
            {(
              [
                { key: "all", label: "Alle" },
                {
                  key: "requested",
                  label: `Angefragt${requestedCount > 0 ? ` (${requestedCount})` : ""}`,
                },
                {
                  key: "rejected",
                  label: `Abgelehnt${rejectedCount > 0 ? ` (${rejectedCount})` : ""}`,
                },
                { key: "confirmed", label: "Bestätigt / aktiv" },
                { key: "checked_out", label: "Abgereist" },
              ] as const
            ).map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={
                  statusFilter === tab.key
                    ? "rounded-full bg-accent px-3 py-1 text-xs font-medium text-white"
                    : "rounded-full border border-separator px-3 py-1 text-xs font-medium text-label-secondary hover:bg-fill-tertiary"
                }
                onClick={() => {
                  setSearchParams({ status: tab.key }, { replace: true });
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <label className="flex max-w-xl flex-col gap-1 text-sm" data-tour="bookings-search">
            <span className="text-label-secondary">Buchungen durchsuchen</span>
            <input
              type="search"
              className="eh-input"
              placeholder="Nr., Gast, Platz, Kennzeichen, Status, Datum…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Buchungen durchsuchen"
              autoComplete="off"
            />
          </label>
          {!loading ? (
            <p className="text-sm text-label-secondary">{bookingCountLabel}</p>
          ) : null}
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          {canEdit ? (
            <Button
              type="button"
              variant="secondary"
              className="w-full sm:w-auto"
              loading={optimizing}
              onClick={() => void runPitchOptimization()}
            >
              Plätze optimieren
            </Button>
          ) : null}
          {canEdit ? (
            <Button
              type="button"
              variant="secondary"
              className="w-full sm:w-auto"
              onClick={() => setPhoneHoldModalOpen(true)}
            >
              Schnellbuchung
            </Button>
          ) : null}
          <Button
            type="button"
            className="w-full sm:w-auto"
            data-tour="bookings-create"
            onClick={() => setCreateModalOpen(true)}
          >
            Neue Buchung
          </Button>
        </div>
      </div>

      <CreateBookingModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        pitches={pitches}
        categories={categories}
        onCreated={load}
      />

      <PhoneBookingHoldModal
        open={phoneHoldModalOpen}
        onClose={() => setPhoneHoldModalOpen(false)}
        categories={categories}
        onCreated={() => {
          load();
        }}
      />

      <GuestStayModal
        open={!!stayModalBooking}
        booking={stayModalBooking}
        guest={stayModalGuest}
        pitch={stayModalPitch}
        canEdit={canEdit}
        userGroups={groups}
        onInvoiceChanged={load}
        onClose={() => setStayModalBooking(null)}
        onConfirm={
          stayModalBooking?.status === "requested"
            ? () => openInquiryEmail(stayModalBooking, "confirm")
            : undefined
        }
        onReject={
          stayModalBooking?.status === "requested"
            ? () => openInquiryEmail(stayModalBooking, "reject")
            : undefined
        }
        onEditBooking={() => {
          if (stayModalBooking) {
            setSelectedBooking(stayModalBooking);
            setStayModalBooking(null);
          }
        }}
        onEditGuest={() => {
          if (stayModalGuest) {
            setSelectedGuest(stayModalGuest);
            setStayModalBooking(null);
          }
        }}
      />

      <InquiryDetailModal
        open={!!inquiryDetailModal}
        booking={inquiryDetailModal}
        guest={inquiryDetailGuest}
        pitches={pitches}
        pitch={inquiryDetailPitch}
        categoryName={inquiryDetailCategory}
        canEdit={canEdit}
        confirmLoading={confirmLoadingId === inquiryDetailModal?.id}
        rejectLoading={rejectLoadingId === inquiryDetailModal?.id}
        onClose={() => setInquiryDetailModal(null)}
        onConfirm={
          inquiryDetailModal
            ? () => openInquiryEmail(inquiryDetailModal, "confirm")
            : undefined
        }
        onReject={
          inquiryDetailModal
            ? () => openInquiryEmail(inquiryDetailModal, "reject")
            : undefined
        }
        onBookingUpdated={applyBookingUpdate}
        onOpenPitch={(pitch) => setSelectedPitch(pitch)}
      />

      <PlaceBanConfirmModal
        open={!!placeBanConfirmModal}
        booking={placeBanConfirmModal}
        loading={placeBanConfirmLoading}
        onClose={() => setPlaceBanConfirmModal(null)}
        onProceed={handlePlaceBanConfirmProceed}
      />

      <InquiryEmailModal
        open={!!inquiryEmailModal}
        bookingId={inquiryEmailModal?.booking.id ?? null}
        bookingNumber={inquiryEmailModal?.booking.booking_number ?? null}
        intent={inquiryEmailModal?.intent ?? "confirm"}
        canEdit={canEdit}
        confirmLoading={confirmLoadingId === inquiryEmailModal?.booking.id}
        rejectLoading={rejectLoadingId === inquiryEmailModal?.booking.id}
        onClose={() => setInquiryEmailModal(null)}
        onSent={load}
        onConfirm={
          inquiryEmailModal
            ? (options) => handleConfirmInquiry(inquiryEmailModal.booking, options)
            : undefined
        }
        onReject={
          inquiryEmailModal
            ? () => handleRejectInquiry(inquiryEmailModal.booking, true)
            : undefined
        }
      />

      <EditBookingModal
        open={!!selectedBooking}
        booking={selectedBooking}
        guest={selectedBookingGuest}
        pitches={pitches}
        categories={categories}
        onClose={() => setSelectedBooking(null)}
        onSaved={load}
        canEdit={canEdit}
        onOpenGuest={openGuestFromBooking}
        onOpenPitch={openPitchFromBooking}
      />

      <EditGuestModal
        open={!!selectedGuest}
        guest={selectedGuest}
        onClose={() => setSelectedGuest(null)}
        onSaved={load}
        canEdit={canEdit}
      />

      <EditPitchModal
        open={!!selectedPitch}
        pitch={selectedPitch}
        categories={categories}
        onClose={() => setSelectedPitch(null)}
        onSaved={load}
        canEdit={canEdit}
      />

      {error && <p className="text-sm text-status-red">{error}</p>}
      {loading ? (
        <p className="text-sm text-label-secondary">Lade Buchungen…</p>
      ) : (
        <div data-tour="bookings-table">
        <Table
          wide
          className="eh-table--bookings"
          isEmpty={sortedBookings.length === 0}
          emptyMessage={
            search.trim()
              ? "Keine Treffer für die Suche."
              : statusFilter === "requested"
                ? "Keine offenen Buchungsanfragen."
                : statusFilter === "rejected"
                  ? "Keine abgelehnten Anfragen."
                  : "Noch keine Buchungen vorhanden."
          }
        >
          <thead>
            <tr>
              <SortableTableHeader
                label="Nr."
                active={sortKey === "booking_number"}
                direction={sortDir}
                onSort={() => handleSortColumn("booking_number")}
              />
              <SortableTableHeader
                label="Gast"
                active={sortKey === "guest"}
                direction={sortDir}
                onSort={() => handleSortColumn("guest")}
              />
              <SortableTableHeader
                label="Platz"
                active={sortKey === "pitch"}
                direction={sortDir}
                onSort={() => handleSortColumn("pitch")}
              />
              <SortableTableHeader
                label="Zeitraum"
                active={sortKey === "arrival"}
                direction={sortDir}
                onSort={() => handleSortColumn("arrival")}
              />
              <SortableTableHeader
                label="Status"
                active={sortKey === "status"}
                direction={sortDir}
                onSort={() => handleSortColumn("status")}
              />
              <SortableTableHeader
                label="Gesamt"
                active={sortKey === "total"}
                direction={sortDir}
                onSort={() => handleSortColumn("total")}
                align="right"
              />
            </tr>
          </thead>
          <tbody>
            {visibleBookings.map((b) => {
              const pitch = b.pitch_id ? pitches.find((x) => x.id === b.pitch_id) : undefined;
              const pitchLabel = bookingPitchLabel(b);
              return (
                <ClickableTableRow
                  key={b.id}
                  ariaLabel={`Buchung ${b.booking_number} öffnen`}
                  onActivate={() => openBookingFromTable(b)}
                >
                  <td className="font-mono text-xs text-label-secondary" data-label="Nr.">
                    {b.booking_number}
                  </td>
                  <td className="font-medium" data-label="Gast">
                    <button
                      type="button"
                      className="text-left text-accent hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        openBookingFromTable(b);
                      }}
                    >
                      {guestName(b.guest_id, b)}
                    </button>
                  </td>
                  <td data-label="Platz">
                    <PitchPlanCell
                      booking={b}
                      pitchLabel={pitchLabel}
                      onPitchClick={pitch ? () => setSelectedPitch(pitch) : undefined}
                    />
                  </td>
                  <td className="text-label-secondary" data-label="Zeitraum">
                    {formatIsoDateShort(b.arrival_date)} → {formatIsoDateShort(b.departure_date)}
                    <span className="ml-1 text-label-tertiary">({b.nights} N.)</span>
                  </td>
                  <td data-label="Status">
                    <StatusBadge status={b.status} />
                  </td>
                  <td className="text-right font-medium tabular-nums" data-label="Gesamt">
                    {formatStayAmount(b.total_amount, b.currency, b.price_pending)}
                  </td>
                </ClickableTableRow>
              );
            })}
            {hasMore ? (
              <tr ref={sentinelRef} aria-hidden>
                <td colSpan={6} className="py-3 text-center text-xs text-label-tertiary">
                  Weitere Buchungen werden geladen…
                </td>
              </tr>
            ) : null}
          </tbody>
        </Table>
        </div>
      )}
    </div>
  );
}
